import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { OutlinedInput } from "@mui/material";
import {
  PageHeader,
  PageSubHeader,
  SectionFlexColumn,
} from "../../../../components/General";
import { Switch } from "@mui/material";
import Slider from "../../../../components/Slider";
import { colors } from "../../../../styles";
import Checkbox from "../../../../components/Checkbox";
import ContentBox, {
  ContentHeader,
  ContentSubHeader,
} from "../../../../components/ContentBox";
import Button from "../../../../components/Button";
import useGetOnboarding from "../../../../hooks/queries/admin/useGetOnboarding";
import { StepComponentProps } from "../index";
import debitCard from "../../../../assets/icons/debitCard.svg";
import useSaveOnboarding, {
  ProgramItemsInterface,
} from "../../../../hooks/mutations/admin/useSaveOnboarding";

const PageContainer = styled("div")(`
    width: 100%;
    height: 100%;
    display: table;
    padding-bottom: 150px;
`);

const HeaderGradient = styled("div")(`
    height: 167px;
    padding-right: 20px;
    padding-left: 20px;
    width: 100%;
    background: linear-gradient(88.81deg, #EFEFFD 0%, #DBF2FC 51.04%, #FFF4E3);
    color: #475467;
    font-size: 20px;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    justify-content: center;
`);

const InnerContainer = styled("div")(`
    margin: 20px 0;
    display: table;
`);

const SecondaryContainer = styled("div")(`
    margin: 20px 0;
    display: flex;
`);

export const ContentTitle = styled("h2")(`
    font-family: Inter;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    color: ${colors.black_01};
    margin: 8px 0px 4px;
`);

const ContentText = styled("h2")(`
    font-family: Inter;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: ${colors.gray};
`);

const switchFormValues: { [key: string]: boolean } = {
  signUpBonusEnabled: false,
  goalBasedMatchEnabled: false,
  noSpendMatchEnabled: false,
  referralBonusEnabled: false,
};

const defaultFormValues: { [key: string]: any } = {
  budget: null,
  ygbm_goal: 0,
  ygbm_reward: 0,
  no_spend_monthly_match_bonus: 0,
  default_contrib_amount: 0,
  sign_up_bonus_amount: 0,
  referral_bonus_amount: 0,
  offer_debit_card: false,
  send_debit_card_by_default: false,
};

const ProgramDefinition = ({ nextStep }: StepComponentProps) => {
  const [values, setValues] = useState({
    ...defaultFormValues,
    ...switchFormValues,
  });

  // Queries
  const { data: initialData } = useGetOnboarding();
  const { mutate: saveOnboarding } = useSaveOnboarding({
    onSuccessHandler: nextStep,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const setValue = (field: string, value: any) =>
    setValues((old) => ({ ...old, [field]: value }));

  useEffect(() => {
    if (initialData) {
      const programData = initialData.program;

      // programData returns more than we need to work with so rather than storing all of them in state
      // just store the ones we need
      setValues({
        budget: programData.budget || defaultFormValues.budget,
        ygbm_goal: programData.ygbm_goal || defaultFormValues.ygbm_goal,
        ygbm_reward: programData.ygbm_reward || defaultFormValues.ygbm_reward,
        ygbm_monthly_max_contrib:
          programData.ygbm_monthly_max_contrib ||
          defaultFormValues.ygbm_monthly_max_contrib,
        no_spend_monthly_match_bonus:
          programData.no_spend_monthly_match_bonus ||
          defaultFormValues.no_spend_monthly_match_bonus,
        default_contrib_amount:
          initialData.program.default_contrib_amount ||
          defaultFormValues.default_contrib_amount,
        sign_up_bonus_amount:
          programData.sign_up_bonus_amount ||
          defaultFormValues.sign_up_bonus_amount,
        referral_bonus_amount:
          programData.referral_bonus_amount ||
          defaultFormValues.referral_bonus_amount,
        offer_debit_card:
          programData.offer_debit_card || defaultFormValues.offer_debit_card,
        send_debit_card_by_default:
          programData.send_debit_card_by_default ||
          defaultFormValues.send_debit_card_by_default,
        // switch form values
        signUpBonusEnabled: !!programData.sign_up_bonus_amount,
        goalBasedMatchEnabled: !!programData.ygbm_goal,
        noSpendMatchEnabled: !!programData.no_spend_monthly_match_bonus,
        referralBonusEnabled: !!programData.referral_bonus_amount,
      });
    }
  }, [initialData]);

  const handleContinue = () => {
    const cleanedData: ProgramItemsInterface = {};

    // remove values we only need on client
    Object.keys(values).forEach((key) => {
      const valuesToRemove = Object.keys(switchFormValues);
      if (!valuesToRemove.includes(key)) {
        let val = values[key]; // make copy
        // if value is empty string, set to null
        // this is to prevent the API from throwing an error for Integer fields
        if (values[key] === "" || values[key] === "0" || values[key] === 0) {
          val = null;
        }

        cleanedData[key] = val;
      }
    });

    saveOnboarding({ programItems: cleanedData });
  };

  const pageCopy = {
    header: "Your Matching Budget",
    subHeader: "Per Participant Per Year",
    contentBlocks: [
      {
        title: "General",
        subTitle: "Define how participants receive savings matches.",
        innerContent: [
          {
            title: "Set default participant contribution (per month)",
            text: "Employees can change their contribution during sign up or in the app at any time.",
            dataKey: "default_contrib_amount",
            hideSwitch: true,
            inputData: [
              {
                inputType: "range",
                range: [30, 250],
                default: values.default_contrib_amount,
                inputDescription: `With this contribution, employees will save $${(
                  values["default_contrib_amount"] * 12
                )?.toLocaleString()} per year.`,
                dataKey: "default_contrib_amount",
              },
            ],
          },
          {
            title: "Sign up bonus",
            text: "Each participant will receive this when they enroll and set a contribution.",
            dataKey: "sign_up_bonus_amount",
            switchDataKey: "signUpBonusEnabled",
            inputData: [
              {
                inputType: "textField",
                default: values.default_contrib_amount,
                inputDescription:
                  "Sign up bonuses are a key variable to drive adoption.",
                dataKey: "sign_up_bonus_amount",
              },
            ],
          },
        ],
      },
      {
        title: "Match Rules",
        subTitle: "Define how participants receive savings matches.",
        innerContent: [
          {
            title: "Goal-based match",
            text: "Employees earn this match upon reaching the Employee Program Goal oncer per year.",
            dataKey: "program_type",
            switchDataKey: "goalBasedMatchEnabled",
            inputData: [
              {
                inputType: "textField",
                default: defaultFormValues.ygbm_goal,
                inputTitle: "Employee Program Savings Goal (per year)",
                inputDescription:
                  "This should feel achievable, yet meaningful for employees.",
                dataKey: "ygbm_goal",
              },
              {
                inputType: "textField",
                default: defaultFormValues.ygbm_reward,
                inputTitle: "Match amount (per year)",
                inputDescription:
                  "This should feel like an exciting incentive to save.",
                dataKey: "ygbm_reward",
              },
            ],
          },
          {
            title: "No-spend monthly match",
            text: "Employees earn this match upon reaching the Employee Program Goal oncer per year.",
            switchDataKey: "noSpendMatchEnabled",
            lineSeparator: true,
            inputData: [
              {
                inputType: "textField",
                default: defaultFormValues.no_spend_monthly_match_bonus,
                inputTitle: "Match amount (per month)",
                inputDescription:
                  "This should feel like an exciting incentive to save.",
                dataKey: "no_spend_monthly_match_bonus",
                switchDataKey: "noSpendMatchEnabled",
              },
            ],
          },
        ],
      },
      {
        title: "Referral Bonus",
        subTitle:
          "What the referring employee receives after their coworker signs up and makes an initial contribution.",
        dataKey: "referral_bonus_amount",
        innerContent: [
          {
            title: "Referral Bonus",
            switchDataKey: "referralBonusEnabled",
            dataKey: "referral_bonus_amount",
            inputData: [
              {
                inputType: "textField",
                default: defaultFormValues.referral_bonus_amount,
                inputDescription: "This can increase virality among employees.",
                dataKey: "referral_bonus_amount",
              },
            ],
          },
        ],
      },
      {
        title: "Access to Funds",
        subTitle:
          "Participants always have quick access to their funds. We never want to stand in the way of an emergency. By default, they can connect an external account and transfer money out whenever they need. You can provide additional options below.",
        innerContent: [
          {
            title: "Offer debit cards to participants",
            subTitle:
              "Debit cards provide immediate access to funds in an emergency, but can lead to spending. We charge an extra $1.50 pppm to offer cards.",
            dataKey: "offer_debit_card",
            switchDataKey: "offer_debit_card",
          },
        ],
        checkContent: [
          {
            title: "Send cards by default",
            text: "If this is enabled, employees will be sent a debit card upon enrolling. If it is not enabled, employees can order one at any time.",
            dataKey: "send_debit_card_by_default",
            hideKey: "offer_debit_card",
            displayIcon: debitCard,
          },
        ],
      },
    ],
  };

  const innerContent = (inputData: any) => {
    return inputData.map((body: any, index: number) => {
      return (
        <SecondaryContainer
          key={index}
          style={{
            borderTop: body.lineSeparator ? "1px solid #e4e7ec" : 0,
            paddingTop: body.lineSeparator ? "20px" : 0,
          }}
        >
          {!body.hideSwitch ? (
            <Switch
              sx={{
                margin: "0",
                "& .MuiSwitch-thumb": {
                  color: colors.white,
                },
                "& .MuiSwitch-switchBase": {
                  "&.Mui-checked": {
                    "& + .MuiSwitch-track": {
                      opacity: 1,
                      backgroundColor: colors.black,
                    },
                  },
                },
                "& .MuiSwitch-track": {
                  backgroundColor: colors.black,
                },
              }}
              checked={values[body.switchDataKey]}
              onClick={(e) => {
                const newValue = !values[body.switchDataKey];
                setValue(body.switchDataKey, newValue);
                // handle switch off
                if (!newValue) {
                  // special handling for some cards
                  switch (body.switchDataKey) {
                    case "offer_debit_card":
                      setValue("send_debit_card_by_default", false);
                      break;
                    case "goalBasedMatchEnabled":
                      setValue("ygbm_goal", 0);
                      setValue("ygbm_reward", 0);
                      break;
                    case "noSpendMatchEnabled":
                      setValue("no_spend_monthly_match_bonus", 0);
                      break;
                    default:
                      setValue(body.dataKey, 0);
                  }
                }
              }}
            />
          ) : (
            <div style={{ marginLeft: 60 }} />
          )}
          <div>
            <ContentTitle>{body.title}</ContentTitle>
            {body.text && <ContentText>{body.text}</ContentText>}
            {body.inputData &&
              body.inputData.map((inputField: any, index: number) => {
                if (inputField.inputType === "textField") {
                  return textFieldInput(inputField, body, index);
                } else if (inputField.inputType === "range") {
                  return sliderInput(inputField, index);
                }
                return null;
              })}
          </div>
        </SecondaryContainer>
      );
    });
  };

  const checkContent = (inputContent: any) => {
    return inputContent.map((body: any, index: number) => {
      if (values[body.hideKey]) {
        return (
          <React.Fragment key={index}>
            <SecondaryContainer key={index}>
              <div style={{ margin: "6px 20px" }}>
                <Checkbox
                  checked={values[body.dataKey]}
                  onClick={() => setValue(body.dataKey, !values[body.dataKey])}
                />
              </div>
              <div style={{ display: "table" }}>
                <ContentTitle>{body.title}</ContentTitle>
                {body.text && <ContentText>{body.text}</ContentText>}
                {body.inputData &&
                  body.inputData.map((inputField: any, index: number) => {
                    if (inputField.type === "textField") {
                      return textFieldInput(inputField, body, index);
                    }
                    return null;
                  })}
              </div>
            </SecondaryContainer>
            {body.displayIcon && (
              <img
                style={{ margin: "4px 60px" }}
                src={body.displayIcon}
                alt="card"
              />
            )}
          </React.Fragment>
        );
      }
      return null;
    });
  };

  const textFieldInput = (inputField: any, inputBody: any, index: number) => {
    return (
      <div style={{ margin: "12px 0px 6px" }} key={index}>
        <ContentTitle>{inputField.inputTitle}</ContentTitle>
        {inputField.subTitle && (
          <ContentText>{inputField.inputDescription}</ContentText>
        )}
        <div style={{ display: "flex", alignItems: "center" }}>
          <OutlinedInput
            id="number-field"
            size="small"
            type="number"
            // @ts-ignore
            value={values[inputField?.dataKey]}
            disabled={!values[inputBody.switchDataKey]}
            startAdornment={<p style={{ marginRight: 1 }}>$</p>}
            onChange={(event) =>
              setValue(inputField?.dataKey, event.target.value)
            }
            sx={{
              color: `${colors.gray_500}`,
              marginRight: "12px",
              width: "100px",
              minWidth: "100px",
              maxWidth: "100px",
              height: "40px",
              fontSize: 14,
            }}
          />
          <ContentText sx={{ margin: "14px 4px" }}>
            {inputField.inputDescription}
          </ContentText>
        </div>
      </div>
    );
  };

  const valuetext = (value: number) => {
    return `$${value}`;
  };

  const sliderInput = (inputField: any, index: number) => {
    return (
      <div style={{ margin: "12px 0px 6px" }} key={index}>
        <ContentTitle>{inputField.inputTitle}</ContentTitle>
        {inputField.subTitle && (
          <ContentText>{inputField.inputDescription}</ContentText>
        )}
        <Slider
          min={30}
          max={250}
          marks={[
            { value: 30, label: "$30" },
            { value: 250, label: "$250" },
          ]}
          getAriaValueText={valuetext}
          valueLabelDisplay="on"
          valueLabelFormat={valuetext}
          value={Number(values[inputField.dataKey])}
          onChange={(e) => {
            setValue(inputField.dataKey, (e.target as HTMLInputElement)?.value);
          }}
        />
        <ContentText sx={{ margin: "14px 4px" }}>
          {inputField.inputDescription}
        </ContentText>
      </div>
    );
  };

  if (initialData) {
    const maxMatchingCost = Math.floor(
      Number(values.sign_up_bonus_amount) +
        Number(values.ygbm_reward) +
        Number(values.no_spend_monthly_match_bonus * 12)
    );
    const averageMatchingCost = Math.floor(maxMatchingCost * 0.6);
    return (
      <PageContainer>
        {values.budget && (
          <HeaderGradient>
            <PageHeader>{pageCopy.header}</PageHeader>
            <PageHeader
              style={{
                fontWeight: "bold",
                fontSize: 48,
                marginTop: 8,
                marginBottom: 16,
              }}
            >
              {values.budget}
            </PageHeader>
            <PageSubHeader>{pageCopy.subHeader}</PageSubHeader>
          </HeaderGradient>
        )}
        {pageCopy.contentBlocks.map((content: any, index: number) => {
          return (
            <React.Fragment key={index}>
              <ContentBox>
                <ContentHeader>{content.title}</ContentHeader>
                <ContentSubHeader>{content.subTitle}</ContentSubHeader>
                <InnerContainer>
                  {content.innerContent && innerContent(content.innerContent)}
                  {content.checkContent && checkContent(content.checkContent)}
                </InnerContainer>
              </ContentBox>
            </React.Fragment>
          );
        })}
        <ContentBox
          style={{
            border: 0,
            padding: 4,
            background:
              "linear-gradient(91.32deg, #DBF2FC 0%, #E1F6F1 51.56%, #FFF4E3 100%)",
          }}
        >
          <div style={{ background: colors.white, padding: "20px 30px" }}>
            <ContentHeader style={{ fontSize: 28, width: "75%" }}>
              Your Employee Matching Program Costs
            </ContentHeader>
            <SectionFlexColumn>
              <ContentTitle style={{ margin: 0 }}>
                Maximum Matching Cost
              </ContentTitle>
              <h3
                style={{
                  fontSize: 48,
                  fontWeight: "bold",
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                ${maxMatchingCost || 0}
              </h3>
              <p style={{ marginTop: 0, fontSize: 12 }}>
                Per Participant Per Year
              </p>
              <p style={{ marginTop: 0, fontSize: 14 }}>
                This is the amount to{" "}
                <span style={{ fontWeight: "bold" }}>budget</span> for employee
                matches assuming that every employee earns every match.
              </p>
            </SectionFlexColumn>
            <div style={{ width: "10%" }}>
              <hr
                style={{
                  width: 64,
                  borderColor: colors.gray_400,
                  height: 0.5,
                }}
              />
            </div>
            <SectionFlexColumn>
              <ContentTitle style={{ marginBottom: 0 }}>
                Estimated Matching Cost
              </ContentTitle>
              <h3
                style={{
                  fontSize: 48,
                  fontWeight: "bold",
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                ${averageMatchingCost || 0}
              </h3>
              <p style={{ marginTop: 0, fontSize: 12 }}>
                Per Participant Per Year
              </p>
              <p style={{ marginTop: 0, fontSize: 14 }}>
                This is the amount to{" "}
                <span style={{ fontWeight: "bold" }}>expect</span> for employee
                matches assuming that every employee earns every match.
              </p>
            </SectionFlexColumn>
          </div>
        </ContentBox>
        <Button onClick={handleContinue}>Continue</Button>
      </PageContainer>
    );
  }
  return null;
};

export default ProgramDefinition;
